import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGetBrokerGroupsQuery, useGetUserDetailsQuery } from '@/features/user/userApi';
import { getComparator, stableSort } from '@/components/table/functions';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { ResponsiveAutocomplete } from '@/components/selects/ResponsiveAutocomplete';

/**
 * Select for the current user's broker groups. Fetches them itself and renders
 * ResponsiveAutocomplete.
 *
 * `brokerGroupId` is a broker group doc id (single) or an array of them (`multiple`), and
 * onChange gets the same. With the "All" option on (showAllOption, or the
 * renewalReportAllOption flag), choosing it in single mode emits 'all'. Options are sorted
 * by groupBy, else name. Other props go to ResponsiveAutocomplete.
 */
export const BrokerGroupSelect = ({
	brokerGroupId,
	onChange,
	multiple = false,
	groupBy,
	showAdmin = false,
	showAllOption = false,
	isGroupDisabled, // Broker groups that cannot be selected
	isGroupFixed, // Broker groups that cannot be removed (multiple only)
	disabled,
	...props
}) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const userQuery = useGetUserDetailsQuery({ email }, { skip: !email });
	const brokerGroupIds = userQuery.data?.brokerGroupArray ?? [];

	const { getFeatureFlags } = useFeatureFlagContext();
	const showAll = showAllOption || Boolean(getFeatureFlags('renewalReportAllOption')?.active);

	const brokerGroupsQuery = useGetBrokerGroupsQuery(
		{ ids: brokerGroupIds, showAdmin: showAdmin && userQuery.data?.isFolioAdmin },
		{ skip: !brokerGroupIds.length },
	);

	const options = useMemo(
		() => stableSort(brokerGroupsQuery.data ?? [], getComparator('desc', groupBy ?? 'name')),
		[brokerGroupsQuery.data, groupBy],
	);

	const isLoading = !brokerGroupsQuery.isSuccess || !userQuery.isSuccess;

	return (
		<ResponsiveAutocomplete
			options={options}
			value={multiple ? (brokerGroupId ?? []) : (brokerGroupId ?? null)}
			onChange={onChange}
			multiple={multiple}
			groupBy={groupBy}
			showAllOption={showAll}
			isOptionDisabled={isGroupDisabled}
			isOptionFixed={isGroupFixed}
			disabled={disabled || isLoading}
			loading={isLoading}
			fitMenuToContent
			{...props}
		/>
	);
};

BrokerGroupSelect.displayName = 'BrokerGroupSelect';
